import React, { useContext } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { generateGuid, getTimestamp } from '@bingads-webui-universal/primitive-utilities';
import _ from 'underscore';

const getAjaxStubLogger = _.memoize((instrumentation) => {
  if (instrumentation && instrumentation.ajaxStubScenario) {
    const scenario = instrumentation.ajaxStubScenario.create();
    const activity = scenario.ajaxStubActivity.create();
    return activity;
  }

  return {
    ajax: _.noop,
    trace: _.noop,
    error: _.noop,
  };
});


const getRequestPayloadLogger = _.memoize((instrumentation) => {
  const schema = {
    scenario: [
      {
        name: 'ReportsV4DataSource',
        value: 'ReportsV4DataSource',
        activity: [
          { name: 'RequestPayload', value: 'RequestPayload' },
          { name: 'Response', value: 'Response' },
        ],
      },
    ],
  };

  instrumentation.addScenario(schema);

  const scenario = instrumentation.ReportsV4DataSource.create();
  const reqestLogger = scenario.RequestPayload.create();
  const responseLogger = scenario.Response.create();

  return {
    reqestLogger,
    responseLogger,
  };
});


function isUserError(userErrorCodes, response) {
  if (response.status === 400) {
    const { bodyText } = response;
    if (bodyText) {
      return !!userErrorCodes.find(code => bodyText.includes(code));
    }
    return false;
  }
  if (response.status === 401) {
    return true;
  }
  return false;
}


async function readBody(response) {
  const bodyText = 'bodyText';
  response[bodyText] = await response.text();
  // response body can only be read once, otherwise will get TypeError: Failed to execute 'text' on 'Response': body stream already read
  response.text = () => Promise.resolve(response[bodyText]);
  response.json = () => Promise.resolve(JSON.parse(response[bodyText]));
}

function hashFn({ userErrorCodes, oDataToken }) {
  return JSON.stringify(userErrorCodes) + oDataToken;
}

export const fetchFactory = _.memoize(({
  host,
  path,
  oDataToken,
  sessionId,
  lcid,
  pageTrackingId,
  redirectUrl,
  instrumentation,
  applicationName = 'bingadsweb',
  userErrorCodes = [],
}) => {
  const ajaxStubLogger = getAjaxStubLogger(instrumentation);
  const { reqestLogger, responseLogger } = getRequestPayloadLogger(instrumentation);

  function beforeFetch(fetchContext) {
    const { options } = fetchContext;

    /* eslint-disable no-param-reassign */
    fetchContext.startTime = getTimestamp();
    fetchContext.httpMethod = options.method || 'GET';
    fetchContext.requestId = options.headers['x-ms-requestid'];

    reqestLogger.trace({ body: options.body }, fetchContext.url, fetchContext.requestId, fetchContext.httpMethod);

    // Mimic AjaxEventHandler::beforeSendHandler
    ajaxStubLogger.ajax(
      fetchContext.requestId,
      fetchContext.url,
      true,
      fetchContext.httpMethod,
      0,
      true,
      '',
      -1
    );
  }


  function afterFetch(fetchContext) {
    const {
      response, startTime, instrument = {},
    } = fetchContext;

    const userError = isUserError(userErrorCodes, response);
    const duration = getTimestamp() - startTime;

    const serverPerf = {
      'x-ms-mte2eelapsedtimems': response.headers.get('x-ms-mte2eelapsedtimems'),
      'x-ms-odataapie2eelapsedtimems': response.headers.get('x-ms-odataapie2eelapsedtimems'),
      'x-ms-odataapionlye2eelapsedtimems': response.headers.get('x-ms-odataapionlye2eelapsedtimems'),
    };

    const serializeFunc = instrument.serializeResponse || (res => ({ count: res['@odata.count'] }));

    response.text().then((text) => {
      try {
        const json = JSON.parse(text);
        responseLogger.trace({ response: serializeFunc(json) }, fetchContext.url, fetchContext.requestId, fetchContext.httpMethod);
      } catch (e) {
        responseLogger.trace({ response: text }, fetchContext.url, fetchContext.requestId, fetchContext.httpMethod);
      }
    });

    // Mimic AjaxEventHandler::successHandler
    ajaxStubLogger.ajax(
      fetchContext.requestId,
      fetchContext.url,
      false,
      fetchContext.httpMethod,
      duration,
      response.ok || userError,
      JSON.stringify(serverPerf),
      -1
    );

    // Mimic AjaxEventHandler::errorHandler
    if (!response.ok) {
      const error = _.compact([
        `Ajax error [${response.status}]`,
        response.statusText || 'error',
        response.bodyText,
      ]).join(', ');

      if (userError) {
        ajaxStubLogger.trace(error, fetchContext.url, fetchContext.requestId, fetchContext.httpMethod);
      } else {
        ajaxStubLogger.error(error, fetchContext.url, fetchContext.requestId, '', fetchContext.httpMethod);
      }
    }

    // Handle token expired
    if (response.status === 401) {
      window.location.href = redirectUrl;
    }
  }


  async function fetch(url, options = {}) {
    const requestId = generateGuid();
    const finalHeaders = {
      authorization: `CCMTSmallToken ${oDataToken}`,
      lcid,
      'content-type': 'application/json',
      'x-ms-applicationname': applicationName,
      'x-ms-lcid': lcid,
      'x-ms-pagetrackingid': pageTrackingId,
      'x-ms-requestid': requestId,
      'x-ms-sessionid': sessionId,
      // Put custom headers at the end to allow overriding default headers
      ...options.headers,
    };

    const finalOptions = {
      ...options,
      headers: finalHeaders,
    };

    const finalUrl = `${host}${path}${url}`;

    const fetchContext = { url: finalUrl, options: finalOptions };

    beforeFetch(fetchContext);

    try {
      const response = await window.fetch(finalUrl, finalOptions);

      await readBody(response);

      fetchContext.response = response;
      afterFetch(fetchContext);

      return response;
    } catch (err) {
      throw new Error(`Request failed, url: ${finalUrl}, error: ${err.toString()}`);
    }
  }

  return { fetch };
}, hashFn);


// Custom hook for function component
export function useFetch({ userErrorCodes = [] } = {}) {
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);

  const domain = serverContext.NonAzureDomain || '';
  const redirectUrl = `${domain}/default.aspx?reason=1&msg=sessionexpired`;

  return fetchFactory({
    host: serverContext.ServicesEndpoints.CampaignAPI,
    path: 'Advertiser/V2',
    oDataToken: serverContext.Token,
    sessionId: serverContext.Omniture.SessionId,
    lcid: serverContext.Omniture.Lcid,
    pageTrackingId: serverContext.PageTrackingId,
    redirectUrl,
    instrumentation,
    userErrorCodes,
  });
}

// HoC for class component
export function withFetch(Comp) {
  return function CompWrapper(props) {
    const dataSourceProps = useFetch();

    return <Comp {...props} {...dataSourceProps} />;
  };
}

